/* eslint-disable import/max-dependencies */
import type { ReactNode, FC } from "react";
import React, { useState } from "react";
import {
  LineChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { Box, Skeleton, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/styles";
import type { Option } from "@fas/cpa-cabinet-ui";
import type { LineInfoType } from "./types/Chart.types";
import LinearGradient from "../LinearGradient";
import GlowShadowFilter from "../GlowShadowFilter";
import ChartLegend from "./ChartLegend";
import ChartYaxesLegend from "./ChartYaxesLegend";
import XAxisContent from "./XAxisContent";
import type { DashboardItemType } from "../../services/deprecatedApi";
import NoData from "../NoData";

const formatDate: (arg0: string) => string = (v) => v;

export type Props = {
  data: DashboardItemType[];
  period: string;
  periods: Option[];
  onChangePeriod: (arg0: string) => unknown;
  isLoading: boolean;
};
const commonLineProps: any = {
  filter: "url(#glowShadow)",
  type: "monotone",
  strokeWidth: 7,
  strokeLinecap: "round",
  dot: false,
  activeDot: false,
  isAnimationActive: false,
};
const colorLine = "#2A3F4E";
const axisStyle: any = {
  fontSize: "12px",
  fill: "rgba(255, 255, 255, 0.6)",
};

const TickComponent = (p: any) => (
  <XAxisContent {...p} colorLine={colorLine} axisStyle={axisStyle} />
);

const Chart: FC<Props> = ({
  data,
  period,
  periods,
  onChangePeriod,
  isLoading,
}: Props) => {
  const theme = useTheme();
  const isMob = useMediaQuery(theme.breakpoints.down("md"));
  const axisStyleLeft = {
    fontSize: "12px",
    fontWeight: "700",
    fill: "#FF9000",
  };
  const axisStyleRight = {
    fontSize: "12px",
    fontWeight: "700",
    fill: "#366DC0",
  };
  const [lines, setLines] = useState<LineInfoType[]>([
    {
      hide: false,
      label: "SmartClicks",
      labelLegend: "Smart Clicks",
      dataKey: "smartClicks",
      colors: ["#FF9000", "#FFD33A"],
      yAxisId: "left",
    },
    {
      hide: false,
      label: "Leads",
      labelLegend: "Leads",
      dataKey: "leads",
      colors: ["#366DC0", "#4589EF"],
      yAxisId: "right",
    },
  ]);

  const onChangeLegend = (key: string, value: boolean) => {
    const index: number = lines.findIndex(({ dataKey }) => dataKey === key);
    const newLines: LineInfoType[] = [
      ...lines.slice(0, index),
      { ...(lines[index] as LineInfoType), hide: value },
      ...lines.slice(index + 1),
    ];
    setLines(newLines);
  };

  const isShowChart = !!data.length;
  const isShowNoData: boolean = !isLoading && data.length === 0;
  const isShowLoading: boolean = !!isLoading && data.length === 0;
  let body: ReactNode = <Box sx={{ width: 1, height: 1 }} />;

  if (isShowLoading) {
    body = (
      <ResponsiveContainer minHeight={150} minWidth={150} aspect={2}>
        <Box py={2} height={1}>
          <Skeleton height="100%" variant="rectangular" />
        </Box>
      </ResponsiveContainer>
    );
  }
  else if (isShowNoData) {
    body = (
      <NoData
        headerText="Oops! Looks like you still have no profit with us."
        text="There are no statistics yet. Don’t worry, we have lots of great offers for you."
      />
    );
  }
  else if (isShowChart) {
    body = (
      <Box
        sx={{
          [theme.breakpoints.only("xs")]: {
            [`& path[filter="${commonLineProps.filter}"]`]: {
              strokeWidth: 3,
            },
          },
        }}
      >
        <ResponsiveContainer minHeight={100} minWidth={200} aspect={2}>
          <LineChart
            /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
            // @ts-ignore
            mirror
            data={data}
            margin={{
              top: 30,
              right: 20,
              left: 0,
              bottom: 20,
            }}
          >
            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.default,
                borderRadius: theme.shape.cardBorderRadius,
                padding: theme.spacing(3),
                boxShadow: "0px 0px 30px rgba(255, 255, 255, 0.2)",
                border: "none",
                marginTop: isMob ? "calc(((100vw - 24px) / 2) - 80px)" : "0",
              }}
              wrapperStyle={{
                outline: "none",
                zIndex: theme.zIndex.tooltip,
              }}
              labelFormatter={formatDate}
              cursor={{
                stroke: colorLine,
              }}
            />
            <CartesianGrid vertical={false} stroke={colorLine} />
            <XAxis
              padding={{ left: 20, right: 20 }}
              dataKey="date"
              tickFormatter={formatDate}
              tick={TickComponent}
              axisLine={{ stroke: colorLine }}
              tickLine={false}
              tickSize={20}
            />

            <defs>
              <GlowShadowFilter id="glowShadow" glowSize={30} />
              {lines.map(({ dataKey, colors }) => (
                <LinearGradient
                  key={dataKey}
                  id={dataKey}
                  colors={colors}
                  lineOffset={0.04}
                />
              ))}
            </defs>

            <YAxis
              width={50}
              tickLine={false}
              axisLine={false}
              tick={axisStyleLeft}
              yAxisId="left"
            />
            <YAxis
              width={50}
              tick={axisStyleRight}
              tickLine={false}
              axisLine={false}
              yAxisId="right"
              orientation="right"
            />

            {lines.map((line) => (
              <Line
                key={line.dataKey}
                {...commonLineProps}
                {...line}
                dataKey={line.dataKey}
                stroke={`url(#${line.dataKey})`}
                name={line.label}
                yAxisId={line.yAxisId}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  }

  return (
    <>
      <ChartLegend
        payload={lines}
        onChangeLegend={onChangeLegend}
        period={period}
        periods={periods}
        onChangePeriod={onChangePeriod}
        disabled={isLoading}
      />
      <ChartYaxesLegend payload={lines} />
      {body}
    </>
  );
};

export default Chart;
